import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAuth0 } from '@auth0/auth0-react'
import { AudioController } from '@chewy/audio-to-notes-ui'
import classNames from 'classnames'
import { Utils } from '@pbt/pbt-ui-components'

import RhapHoverTooltip, {
  type RhapHoverTooltipProps,
} from '~/components/common/RhapHoverTooltip'
import { setInitializingSession } from '~/store/duck/audioRecorder'
import { getAssignedVet } from '~/store/reducers/soap'

import { useAudioRecorderCallbacks } from './hooks/useAudioRecorderCallbacks'
import { useAudioRecorderDisabled } from './hooks/useAudioRecorderDisabled'
import { useAudioRecorderEnvironment } from './hooks/useAudioRecorderEnvironment'
import { useAudioRecorderMetadata } from './hooks/useAudioRecorderMetadata'
import { useAudioRecorderNavigationGuard } from './hooks/useAudioRecorderNavigationGuard'
import { useAudioRecorderSession } from './hooks/useAudioRecorderSession'
import { useAudioRecorderVisibility } from './hooks/useAudioRecorderVisibility'
import { useSummaryContext } from './hooks/useSummaryContext'
import MicrophonePermissionModal from './modals/MicrophonePermissionModal'
import NoAudioDetectedModal from './modals/NoAudioDetectedModal'

import styles from './AudioRecorderHeader.module.scss'

interface AudioRecorderHeaderProps {
  /** Whether to show extra margin for BusinessLocationSwitch dropdown */
  showBusinessSwitchMargin?: boolean
}

const AudioRecorderHeader = ({
  showBusinessSwitchMargin = false,
}: AudioRecorderHeaderProps) => {
  const dispatch = useDispatch()
  const { getAccessTokenSilently } = useAuth0()
  const visibility = useAudioRecorderVisibility()
  const session = useAudioRecorderSession()
  const disabledState = useAudioRecorderDisabled()
  const { disabled } = disabledState
  const disabledReason = disabled ? disabledState.disabledReason : undefined
  useAudioRecorderMetadata() // Initialize metadata
  const environment = useAudioRecorderEnvironment()
  const summaryContext = useSummaryContext()
  const assignedVet = useSelector(getAssignedVet)

  // Modal state
  const [isMicPermissionOpen, setIsMicPermissionOpen] = useState(false)
  const [isNoAudioOpen, setIsNoAudioOpen] = useState(false)
  const [noAudioCallbacks, setNoAudioCallbacks] = useState<{
    onContinueRecording?: () => void
    onEndRecording?: () => void
  }>({})

  const callbacks = useAudioRecorderCallbacks({
    onOpenMicPermissionModal: () => setIsMicPermissionOpen(true),
    onOpenNoAudioModal: (cbs) => {
      setNoAudioCallbacks(cbs)
      setIsNoAudioOpen(true)
    },
  })

  // Navigation guard (beforeunload only)
  useAudioRecorderNavigationGuard()

  // Skip session initialization until we have:
  // 1. A valid correlationId (appointmentId)
  // 2. Medical history fetch has completed (ready or failed) - TEMPORARILY DISABLED
  //    TODO: Re-enable when backend can handle medical history metadata
  //    This prevents initialization API calls with empty correlationId or before medical history is ready
  const hasValidCorrelationId = Boolean(session.correlationId)
  const shouldSkipInitialization = !hasValidCorrelationId
  // Temporarily disabled - waiting on backend support for medical history
  // || !isMedicalHistoryReady

  // Track previous skip state to detect when initialization should start
  const prevShouldSkipRef = useRef(shouldSkipInitialization)

  // Set initialization state synchronously (before paint) when we're about to start initialization
  // This prevents the brief flash where the recorder enables before the API call completes
  // useLayoutEffect runs synchronously after all DOM mutations but before the browser paints,
  // ensuring the Redux state is updated before useAudioRecorderDisabled runs
  useLayoutEffect(() => {
    const isSkipping = shouldSkipInitialization

    // When not skipping (either transitioning from skipping, or not skipping from initial render),
    // initialization is about to start or already started
    if (!isSkipping) {
      dispatch(setInitializingSession(true))
    }
    // When skipping, ensure initialization state is false
    else {
      dispatch(setInitializingSession(false))
    }

    prevShouldSkipRef.current = isSkipping
  }, [shouldSkipInitialization, dispatch])

  // Track initialization state from AudioController (for when it completes)
  const handleControllerStateReady = useCallback(
    (controller: { isInitializingSession: boolean }) => {
      dispatch(setInitializingSession(controller.isInitializingSession))
    },
    [dispatch],
  )

  if (!visibility.isVisible) {
    return null
  }

  // Extract just the metadata object (not the ready flag) for AudioController
  // Medical History commented out until BE is ready
  // currently only supports 10k bytes, not enough for these objects
  const doctorsName = assignedVet
    ? Utils.getPersonString(assignedVet)
    : undefined
  const metadata = {
    // medicalHistory,
    ...(doctorsName && { doctors_name: doctorsName }),
  }

  const renderTrigger = (
    triggerProps: Parameters<RhapHoverTooltipProps['trigger']>[0],
  ) => (
    <span
      {...triggerProps}
      className={classNames(styles.wrapper, {
        [styles.withBusinessSwitchMargin]: showBusinessSwitchMargin,
      })}
    >
      <AudioController
        callbacks={callbacks}
        config={{
          environment, // Auto-resolves API URLs
          skipSessionInitialization: shouldSkipInitialization, // Skip until appointmentId (and medical history when re-enabled)
          cleanupOnUnmount: {
            clearSessionState: true,
            resetLifecycleState: true,
          },
        }}
        disabled={disabled}
        getAccessToken={getAccessTokenSilently}
        metadata={metadata}
        session={session}
        useStopIconForSummarize={!(summaryContext.summariesFTEnabled ?? false)}
        onControllerStateReady={handleControllerStateReady}
      />
    </span>
  )

  return (
    <>
      <RhapHoverTooltip
        noWrap
        content={disabledReason}
        enabled={disabled}
        placement="bottom"
        trigger={renderTrigger}
      />
      <MicrophonePermissionModal
        open={isMicPermissionOpen}
        onClose={() => setIsMicPermissionOpen(false)}
      />
      <NoAudioDetectedModal
        open={isNoAudioOpen}
        onClose={() => setIsNoAudioOpen(false)}
        onContinueRecording={noAudioCallbacks.onContinueRecording}
        onEndRecording={noAudioCallbacks.onEndRecording}
      />
    </>
  )
}

export default AudioRecorderHeader
